import { useState, useEffect } from "react";
import { initialServices } from "./initialServices";

const LANGUAGE_PRESETS = {
  en: {
    url: "https://example.com/en",
    userTitle: "Welcome to the verification center",
    linkMask: "By checking the box next to this text, I sign",
  },
  ru: {
    url: "https://example.com/ru",
    userTitle: "Добро пожаловать в центр верификации",
    linkMask: "Ставя галочку возле этого текста я подписываю",
  },
  kk: {
    url: "https://example.com/kk",
    userTitle: "Тексеру орталығына қош келдіңіз",
    linkMask: "Осы мәтіннің жанына құсбелгі қойып мен қол қоямын",
  },
};

export const initializeServices = (language, services) => {
  const presetValues = LANGUAGE_PRESETS[language];
  return services.map((service) => {
    if (service.paramName === "consent") {
      return {
        ...service,
        params: service.params.map((param) => ({
          ...param,
          value: presetValues?.[param.id] || param.value,
        })),
      };
    }
    return service;
  });
};

export const useFlowServices = (language) => {
  const [services, setServices] = useState(() =>
    initializeServices(language, initialServices)
  );
  const [enabledServices, setEnabledServices] = useState([]);
  const [movingServiceId, setMovingServiceId] = useState(null);
  const [direction, setDirection] = useState("");

  useEffect(() => {
    const inletService = enabledServices.find(
      (s) => s.paramName === "device-filter" && s.type === "inlet"
    );
    if (inletService) {
      if (!services.some((s) => s.paramName === "device-filter" && s.type === "outlet")) {
        setServices((prev) => [
          ...prev,
          { id: 10, name: "Device Switch Outlet", isActive: false, paramName: "device-filter", type: "outlet", params: [] },
        ]);
      }
    } else {
      setServices((prev) =>
        prev.filter((s) => !(s.paramName === "device-filter" && s.type === "outlet"))
      );
    }
  }, [enabledServices]);

  useEffect(() => {
    setServices((prev) => initializeServices(language, prev));
  }, [language]);

  const clearServices = () => {
    setEnabledServices([]);
    setServices(initializeServices(language, initialServices));
  };

  const toggleService = (id) => {
    const service = services.find((s) => s.id === id);
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
    if (!service.isActive) {
      setEnabledServices((prev) => [...prev, service]);
    } else {
      setEnabledServices((prev) => prev.filter((s) => s.id !== id));
    }
  };

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    const src = services[source.index];
    const dst = services[destination.index];
    if (dst.paramName === "consent" || src.paramName === "consent") return;
    const reordered = Array.from(services);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setServices(reordered);
  };

  const moveService = (id, dir) => {
    const index = services.findIndex((s) => s.id === id);
    const newIndex = dir === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;
    setMovingServiceId(id);
    setDirection(dir);
    setTimeout(() => {
      const newServices = Array.from(services);
      const [moved] = newServices.splice(index, 1);
      newServices.splice(newIndex, 0, moved);
      setServices(newServices);
      setTimeout(() => {
        setMovingServiceId(null);
        setDirection("");
      }, 750);
    }, 0);
  };

  const getActiveServiceNumbers = () => {
    return services
      .filter((s) => s.isActive)
      .map((s, index) => ({ id: s.id, number: index + 1 }));
  };

  const updateService = (serviceId, updateParams) => {
    setServices((prev) =>
      prev.map((service) => {
        if (service.id !== serviceId) return service;
        return { ...service, params: updateParams(service.params) };
      })
    );
  };

  const updateInputParam = (serviceId, paramId, value) => {
    updateService(serviceId, (params) =>
      params.map((p) => (p.id === paramId ? { ...p, value } : p))
    );
  };

  const updateCheckbox = (serviceId, paramId) => {
    updateService(serviceId, (params) =>
      params.map((p) => (p.id === paramId ? { ...p, enabled: !p.enabled } : p))
    );
  };

  const updateSelectParam = (serviceId, paramId, value) => {
    updateService(serviceId, (params) =>
      params.map((p) => (p.id === paramId ? { ...p, value } : p))
    );
  };

  const updateRadioGroup = (serviceId, paramId, selectedValue) => {
    updateService(serviceId, (params) =>
      params.map((p) => {
        if (p.id !== paramId) return p;
        const updatedOptions = p.options.map((option) => {
          if (option.value === selectedValue) {
            const updatedChildParams = option.params?.map((cp) => ({ ...cp, enabled: true }));
            return { ...option, enabled: true, ...(updatedChildParams && { params: updatedChildParams }) };
          }
          return { ...option, enabled: false };
        });
        return { ...p, value: selectedValue, options: updatedOptions };
      })
    );
  };

  const updateChildCheckbox = (serviceId, paramId, optionValue, childParamId, isEnabled) => {
    updateService(serviceId, (params) =>
      params.map((p) => {
        if (p.id !== paramId) return p;
        const updatedOptions = p.options.map((option) => {
          if (option.value !== optionValue || !option.params) return option;
          return {
            ...option,
            params: option.params.map((cp) =>
              cp.id === childParamId ? { ...cp, enabled: isEnabled } : cp
            ),
          };
        });
        return { ...p, options: updatedOptions };
      })
    );
  };

  const updateChildParams = (childParams, paramId, isEnabled) => {
    return childParams.map((cp) => {
      if (cp.id === paramId) return { ...cp, enabled: isEnabled };
      if (cp.params) return { ...cp, params: updateChildParams(cp.params, paramId, isEnabled) };
      return cp;
    });
  };

  const updateCheckboxNested = (serviceId, paramId, isEnabled) => {
    const updatedServices = services.map((service) => {
      if (service.id === serviceId) {
        const updatedParams = service.params.map((p) => {
          if (p.id === paramId) return { ...p, enabled: isEnabled };
          if (p.params) return { ...p, params: updateChildParams(p.params, paramId, isEnabled) };
          return p;
        });
        return { ...service, params: updatedParams };
      }
      if (paramId === "Include client card as a document" && service.paramName === "client-card") {
        return { ...service, isActive: isEnabled };
      }
      return service;
    });
    setServices(updatedServices);

    if (paramId === "Include client card as a document") {
      const clientCardService = services.find((s) => s.paramName === "client-card");
      if (clientCardService) {
        if (isEnabled) {
          setEnabledServices((prev) =>
            prev.some((s) => s.paramName === "client-card") ? prev : [...prev, clientCardService]
          );
        } else {
          setEnabledServices((prev) => prev.filter((s) => s.paramName !== "client-card"));
        }
      }
    }
  };

  const updateChildSelectParams = (childParams, paramId, value) => {
    return childParams.map((cp) => {
      if (cp.id === paramId) return { ...cp, value };
      if (cp.params) return { ...cp, params: updateChildSelectParams(cp.params, paramId, value) };
      return cp;
    });
  };

  const updateSelectParamNested = (serviceId, paramId, value) => {
    updateService(serviceId, (params) =>
      params.map((p) => {
        if (p.id === paramId) return { ...p, value };
        if (p.params) return { ...p, params: updateChildSelectParams(p.params, paramId, value) };
        return p;
      })
    );
  };

  return {
    services,
    setServices,
    enabledServices,
    setEnabledServices,
    movingServiceId,
    direction,
    clearServices,
    toggleService,
    onDragEnd,
    moveService,
    activeServiceNumbers: getActiveServiceNumbers(),
    updateInputParam,
    updateCheckbox,
    updateSelectParam,
    updateRadioGroup,
    updateChildCheckbox,
    updateCheckboxNested,
    updateSelectParamNested,
  };
};
